import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getAuthorizedUser } from "src/actions/auth";
import { clearUserDetails, manageUserStatus } from "src/actions/user";
import Checkbox from "src/components/Shared/Forms/Inputs/Checkbox";
import SelectMenu from "src/components/Shared/Forms/Selects/SelectMenu";
import QuickMenu from "src/components/Shared/QuickMenu";
import TableRow from "src/components/Shared/Table/TableRow";
import UserProfile from "src/components/Users/UserProfile";
import { authorizeUserComponentAccess } from "src/helpers/authorizeUserComponentAccess";
import { classNames } from "src/helpers/classNames";
import { getDomoWorkspaceList } from "src/actions/page";
import { toast } from "react-toastify";
import Button from "src/components/Shared/Buttons/Button";
import { formatDate } from "src/helpers/formatDate";

const UserEntry = ({
  user,
  clearUserDetails,
  manageUserStatus,
  getPages,
  setDeleteId,
  lastRow,
  me,
  site,
  getAuthorizedUser,
  allowMulti = false,
  disableCheckButton = false,
  checkedUsers = [],
  setCheckedUsers = () => {},
  refreshUsers = () => {},
  manageUserRole = () => {},
  workspaceDetails = {},
  removeUserAccess = () => {},
  ...props
}) => {
  const navigate = useNavigate();
  const URLParams = useParams();

  const roles = [
    { key: "Participant", value: "participant" },
    { key: "Editor", value: "editor" },
    { key: "Privileged", value: "privileged" },
    { key: "Admin", value: "admin" },
  ];

  const [activeStatus, setActiveStatus] = useState(true);

  useEffect(() => {
    setActiveStatus(user.active_status);
  }, [user.active_status]);

  const insertTypes = {
    SSO_LOGIN: "SSO",
    MANUAL_INPUT: "Manually",
    BULK_UPLOAD: "Bulk",
    DOMO_DATASET: "Dataset",
  };

  const setPreviewUserId = async (id) => {
    try {
      let url;
      const responseList = await getDomoWorkspaceList({ user_id: id });
      if (responseList?.workspaces?.length > 0) {
        if (responseList?.workspaces?.length === 1) {
          if (responseList?.workspaces[0].workspace_type === "JWT_FULL_EMBED") {
            url = `/workspace/${responseList?.workspaces[0]?.slug || responseList?.workspaces[0]._id}/embed`;
          } else {
            if (responseList?.individualPermission.length > 0) {
              url = `/workspace/${responseList?.individualPermission[0].workspace_slug || responseList?.individualPermission[0].workspace_id}/page/${responseList?.individualPermission[0].page_slug || responseList?.individualPermission[0]._id}`;
            } else {
              url = `/workspace/${responseList?.groups[0].workspace_slug || responseList?.groups[0].workspace_id}/group/${responseList?.groups[0].slug || responseList?.groups[0]._id}/page/${
                responseList?.groups[0].permissions[0].page_slug || responseList?.groups[0].permissions[0]._id
              }`;
            }
          }
        } else {
          url = "/workspaces/menu";
        }
      } else {
        toast.error("Currently user doesn't have any page assigned.")
      }

      if (url) {
        localStorage.setItem("impersonatedUserId", id);
        const ac = new AbortController();
        await getAuthorizedUser(ac.signal);
        navigate(url);
      }
    } catch (error) {
      console.dir("error", error);
    }
  };

  const navigateUserMenu = (menuOption) => {
    navigate(user?.type === "admin" ? `/admins/${user._id}?tab=${menuOption}` : `/users/${user._id}?tab=${menuOption}`);
  };

  const isWorkspaceRoute = window.location.href.includes("workspaces") && URLParams?.id;

  const quickMenuItems = [
    {
      name: "View Profile",
      hideWhen: (user.type === "admin" && me?.type !== "admin") || !authorizeUserComponentAccess(me, workspaceDetails?._id, "user", ["update"]),
      onClick: () => {
        clearUserDetails(null);
        navigateUserMenu("View+Profile");
      },
    },
    {
      name: "Workspaces",
      hideWhen: me?.type !== "admin" || !authorizeUserComponentAccess(me, workspaceDetails?._id, "user"),
      onClick: () => {
        clearUserDetails(null);
        navigateUserMenu("Workspaces");
      },
    },
    {
      name: "Permissions",
      hideWhen: (user.type === "admin" && me?.type !== "admin") || (me?.type !== "admin" && !authorizeUserComponentAccess(me, workspaceDetails?._id, "user")) || (!authorizeUserComponentAccess(me, workspaceDetails?._id, "permission", ["update"]) && !authorizeUserComponentAccess(me, workspaceDetails?._id, "permission", ["editor_create"]) && !authorizeUserComponentAccess(me, workspaceDetails?._id, "permission", ["editor_update"])),
      onClick: () => {
        clearUserDetails(null);
        navigateUserMenu("Editors");
      },
    },
    {
      name: "Data Access",
      hideWhen: me?.type !== "admin",
      onClick: () => {
        clearUserDetails(null);
        navigateUserMenu("Data+Access");
      },
    },
    {
      hideWhen: user.default_user,
      type: "hr",
    },
    {
      name: `Impersonate`,
      hideWhen: user.type === 'admin' || me?.type !== "admin",
      isBeta: true,
      onClick: () => {
        setPreviewUserId(user._id);
      },
    },
    {
      name: "Remove Access",
      hideWhen: me?.type !== "admin" || !isWorkspaceRoute || !authorizeUserComponentAccess(me, workspaceDetails?._id, "permission", ["update"]),
      isBeta: false,
      onClick: () => removeUserAccess(user._id),
    },
    {
      name: "Delete",
      hideWhen:
        user.default_user ||
        (user?.type === "admin" && workspaceDetails?._id) ||
        site.api_state === "DOMO_DATASET" ||
        !authorizeUserComponentAccess(me, workspaceDetails?._id, "user", ["update"]) ||
        (me?.type !== "admin" && user?.type === "admin") ||
        me?._id === user?._id ||
        site?.api_state === "DOMO_DATASET",
      onClick: () => {
        setDeleteId(user._id);
      },
      color: "text-red-500",
    },
  ]

  const hasNoHiddenItems = quickMenuItems.filter(item => !item.hideWhen && item.type !== 'hr')

  return (
    <TableRow
      keyId={user?._id}
      lastRow={lastRow}
      classes={hasNoHiddenItems.length > 0 ? "" : "!opacity-50"}
    >
      {allowMulti && me?.type === "admin" && (
        <td className="whitespace-nowrap py-3 pl-1 text-sm sm:pl-4">
          <Checkbox
            isChecked={checkedUsers.includes(user._id)}
            onChange={() => {
              if (disableCheckButton) return;
              setCheckedUsers((cur) => {
                if (cur.includes(user._id)) {
                  return cur.filter((item) => item !== user._id);
                } else {
                  return [...cur, user._id];
                }
              });
            }}
          />
        </td>
      )}
      <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm">
        <div className="flex items-center">
          <div className="hidden h-10 w-10 flex-shrink-0 overflow-hidden rounded-full sm:block">{<UserProfile user={user || {}} />}</div>
          <div className="sm:ml-4">
            <div className="flex items-center gap-x-2 font-medium text-gray-900">
              {authorizeUserComponentAccess(me, workspaceDetails?._id, "user", ["update"]) && hasNoHiddenItems.length > 0 ? (
                <Link
                  to={user?.type === "admin" ? `/admins/${user._id}` : `/users/${user._id}`}
                  className="text-gray-900 font-medium cursor-pointer hover:underline">
                  {user?.name}
                </Link>
              ) : (
                user?.name
              )}
              {user?.default_user && <span className="rounded-full bg-gray-100 px-2 py-[1px] text-gray-400">Admin</span>}
            </div>

            <div className="text-gray-500">
              {insertTypes[user.creation_type]} created {formatDate(user.created_at, "MM/DD/YYYY")}
            </div>
          </div>
        </div>
      </td>
      <td className="hidden whitespace-nowrap px-3 py-3 text-sm text-gray-500 sm:table-cell">
        <div className="text-gray-900">{user?.email}</div>
      </td>
      <td className="hidden whitespace-nowrap px-3 py-3 text-sm text-gray-500 lg:table-cell">
        <Button
          onClick={async () => {
            await manageUserStatus(user);
            refreshUsers();
            setActiveStatus(!activeStatus);
          }}
          version="default"
          className={classNames("flex w-[52px] justify-center rounded-full px-2 text-xs font-semibold leading-5", activeStatus ? "bg-green-200 text-green-700 opacity-50" : "bg-gray-50 text-gray-500", me?.type !== "admin" ? "pointer-events-none cursor-default" : "")}>
          {activeStatus ? "Active" : "Inactive"}
        </Button>
      </td>
      {workspaceDetails?.workspace_type === "JWT_FULL_EMBED" && (
        <td className="hidden whitespace-nowrap px-3 py-3 text-sm text-gray-500 lg:table-cell">
          <SelectMenu
            options={roles}
            setOption={(e) => manageUserRole(e, user)}
            startIndex={roles.findIndex((rl) => rl.value === user.type)}
          />
        </td>
      )}
      <td className="relative flex justify-end whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium sm:pr-6">
        <QuickMenu
          items={quickMenuItems}
          disabled={!hasNoHiddenItems.length}
        />
      </td>
    </TableRow>
  );
};

const mapStateToProps = (state) => {
  return {
    me: state.auth.user,
    site: state.site,
  };
};

export default connect(mapStateToProps, { clearUserDetails, manageUserStatus, getAuthorizedUser })(UserEntry);
